import type { Configuration } from '../../../../../types/Configuration';
import type { ECommerceEventData, EcommerceItemWithDetails } from '../../../../../types/analytics/ECommerce';
import { type Experience, OrderStatus } from '../../../../../types/CoreShoppingExperience';
import isProductEquals from '../../products/isProductEquals';
import convertToECommerceEvent from './convertToECommerceEvent';
import convertToProductDetails from './convertToProductDetails';
import convertOrderToItem from './convertOrderToItem';

const convertToRefundEvent = (
  requestId: string,
  orderId: string,
  experience: Experience,
  partnerConfig: Configuration,
): ECommerceEventData => {
  const product = experience.summaryTrips
    .flatMap((x) => x.steps)
    .flatMap((x) => x.products)
    .find((x) => x.orders.find((order) => order.id === orderId));

  if (product === undefined) {
    throw new Error('Failed to find the related product.');
  }

  const order = product.orders.find((order) => order.id === orderId);
  const foundProduct = experience.purchasableTrips
    .flatMap((x) => x.steps)
    .flatMap((x) => x.products)
    .find((x) => isProductEquals(x.productDetails, product.productDetails));
  const variants = [
    ...(foundProduct?.variants ?? []),
    // When cancelling a "PS on an offer page" that comes from a product.fallbackVariant, we need to find that fallbackVariant offer
    // TODO: The following will be cleaned up after the "PS as a Fallback" experiment (https://plusgrade.jira.com/browse/DSB-3881)
    ...(foundProduct?.fallbackVariants ?? []),
  ];
  const variantIndex = variants?.findIndex((x) => x.upgradeType === order?.upgradeType);

  if (variants === undefined || variantIndex === undefined || variantIndex < 0) {
    throw new Error('Failed to find the related variant.');
  }

  const variant = variants.at(variantIndex);

  if (variant === undefined || order === undefined) {
    throw new Error('Failed to find the related order.');
  }

  const items: EcommerceItemWithDetails[] = [
    {
      ...convertOrderToItem(order, variant),
      ...convertToProductDetails(product.productDetails),
      index: variantIndex,
    },
  ];

  // Check for fallback child offer
  let fallbackOrder;
  const fallbackVariants = experience.purchasableTrips
    .flatMap((x) => x.steps)
    .flatMap((x) => x.products)
    .find((x) => isProductEquals(x.productDetails, product.productDetails))?.fallbackVariants;
  if (fallbackVariants) {
    // Check if status can be modified
    fallbackOrder = experience.summaryTrips
      .flatMap((x) => x.steps)
      .flatMap((x) => x.products)
      .flatMap((x) => x.orders)
      .find(
        (order) =>
          order.encryptedVariantId === fallbackVariants[0].encryptedId && order.status === OrderStatus.CAN_MODIFY,
      );

    if (fallbackOrder) {
      items.push({
        ...convertOrderToItem(fallbackOrder, fallbackVariants[0]),
        ...convertToProductDetails(product.productDetails),
        isFallback: true,
      });
    }
  }

  return convertToECommerceEvent(requestId, items, partnerConfig);
};

export default convertToRefundEvent;
